import { nanoid } from 'nanoid'
import type {
  AgentChat,
  StreamChunk,
  AgentMessage,
  AgentArtifact,
  SendMessagePayloadWithContent,
  AgentProcessSummary,
  MessagesPage,
  Activity,
  ActivitySummary,
  ActivitySummaryParams,
} from '../types'
import type { GetMessagesOptions } from './AgentTransport'
import type {
  DataWithCursor
} from '@multiplayer-app/ai-agent-types'
import { MessageRole, StreamChunkType, ChatType, AgentStatus } from '../types'
import type { AgentConfiguration } from '@multiplayer-app/ai-agent-types'
import type { ToolRegistry } from '../ToolRegistry'
import type {
  AgentTransport,
  ActivityTreeNode,
  UpsertAgentPreferencesRequest,
  UpsertAgentPreferencesResponse,
  UpsertAgentPreferencesBatchRequest,
  UpsertAgentPreferencesBatchResponse
} from './AgentTransport'
import type { DirectTransportConfig, AgentToolDefinition } from '../../config/types'
import { chatStorage } from './ChatStorage'
import type { ModelOption } from '@multiplayer-app/ai-agent-types'
import { ChatStateEntry } from '@/state/useAgentStore'

const decoder = new TextDecoder()

/** Number of most-recent messages to fetch after streaming completes. Matches ProxyTransport. */
const POST_STREAM_MESSAGES_LIMIT = 3

// Minimal shape for OpenAI-style streaming chunks used in direct transport.
// Some models (e.g. DeepSeek R1 via OpenRouter, OpenAI o3 family) also emit
// a `reasoning` field on the delta which we surface as StreamChunkType.Reasoning.
type OpenAIStreamChunk = {
  choices?: Array<{
    delta?: {
      content?: string
      // Optional reasoning trace token for models that support it.
      // For now we treat this as opaque markdown-ish text and stream it
      // separately to the UI where it is aggregated into AgentMessage.reasoning.
      reasoning?: string
      // We intentionally ignore other delta fields (role, tool calls, etc.)
    }
  }>
}

export class DirectTransport implements AgentTransport {
  private chats = new Map<string, AgentChat>()
  private chatState = new Map<string, ChatStateEntry>()
  private toolRegistry?: ToolRegistry
  private streamControllers = new Map<string, AbortController>()
  private contextKeys?: Array<{ key: string; tools: string[] }>
  private initialized = false
  private models?: ModelOption[]
  private untitledChatText = 'Untitled chat'

  constructor(private config: DirectTransportConfig) {
    if (config.mode !== 'direct') {
      throw new Error('DirectTransport requires direct mode configuration')
    }
    if (!config.model) {
      throw new Error('DirectTransport requires a model to be specified')
    }
    if (!config.apiKey) {
      throw new Error('DirectTransport requires an API key. Set VITE_LLM_API_KEY in your .env file')
    }
    this.initializeStorage().catch((error) => {
      console.error('Failed to initialize chat storage:', error)
    })
  }

  updateConfig(next: DirectTransportConfig) {
    if (next.mode !== 'direct') {
      throw new Error('DirectTransport.updateConfig requires direct mode configuration')
    }
    if (!next.model) {
      throw new Error('DirectTransport requires a model to be specified')
    }
    if (!next.apiKey) {
      throw new Error('DirectTransport requires an API key')
    }
    this.config = next
  }

  /**
   * Optional context key metadata used for tool filtering.
   * For the legacy direct transport implementation, we simply
   * store this so the runtime type-checks correctly. Tool
   * selection logic can be wired to this.contextKeys in future
   * without changing the public API.
   */
  setContextKeys(contextKeys: Array<{ key: string; tools: string[] }>) {
    this.contextKeys = contextKeys
  }

  private async initializeStorage(): Promise<void> {
    if (this.initialized) return

    try {
      const chats = await chatStorage.listChats()
      chats.forEach((chat) => {
        this.chats.set(chat.id, chat)
      })
      this.initialized = true
    } catch (error) {
      console.error('Failed to load chats from IndexedDB:', error)
      this.initialized = true
    }
  }

  private generateLocalTitle(content: string): string {
    const firstLine =
      content
        .split('\n')
        .map((line) => line.trim())
        .find((line) => line.length > 0) ?? ''

    const cleaned = firstLine
      .replace(/^(\#{1,6}\s+|\d+\.\s+|[-*+]\s+)/, '')
      .replace(/\s+/g, ' ')
      .trim()

    if (!cleaned) return this.untitledChatText

    const maxLength = 80
    if (cleaned.length <= maxLength) return cleaned

    const sliced = cleaned.slice(0, maxLength)
    const lastSpace = sliced.lastIndexOf(' ')
    if (lastSpace > maxLength * 0.6) {
      return `${sliced.slice(0, lastSpace)}…`
    }
    return `${sliced}…`
  }

  setToolRegistry(registry: ToolRegistry) {
    this.toolRegistry = registry
  }

  setModels(models: ModelOption[]) {
    this.models = models
  }

  setUntitledChatText(text: string) {
    this.untitledChatText = text
  }

  private toolsToFunctions(contextKey?: string): Array<Record<string, unknown>> | undefined {
    if (!this.toolRegistry) return undefined

    const allTools = this.toolRegistry.list()
    if (allTools.length === 0) return undefined

    // Filter tools by context if contextKey is provided
    const availableTools = contextKey
      ? allTools.filter((tool) => {
          // Check if tool is available for this context
          // This would need to be checked against config.contextKeys
          return true // For now, include all tools
        })
      : allTools

    return availableTools.map((tool) => this.toolToFunction(tool))
  }

  private toolToFunction(tool: AgentToolDefinition): Record<string, unknown> {
    return {
      type: 'function',
      function: {
        name: tool.name,
        description: tool.description || tool.label,
        parameters: this.normalizeSchema(tool.schema)
      }
    }
  }

  private normalizeSchema(schema: unknown): {
    type: 'object'
    properties: Record<string, unknown>
    required: string[]
  } {
    if (!schema || typeof schema !== 'object') {
      return { type: 'object', properties: {}, required: [] }
    }

    const rawSchema = schema as {
      properties?: Record<string, unknown>
      required?: unknown
    }

    const properties: Record<string, unknown> = {}
    if (rawSchema.properties && typeof rawSchema.properties === 'object') {
      for (const [key, value] of Object.entries(rawSchema.properties)) {
        properties[key] = this.normalizePropertySchema(value)
      }
    }

    const required =
      Array.isArray(rawSchema.required) && rawSchema.required.every((field) => typeof field === 'string')
        ? (rawSchema.required as string[])
        : []

    return {
      type: 'object',
      properties,
      required
    }
  }

  private normalizePropertySchema(property: unknown): Record<string, unknown> {
    if (!property || typeof property !== 'object' || Array.isArray(property)) {
      return {}
    }

    const prop = property as Record<string, unknown>
    const type = typeof prop.type === 'string' ? prop.type : undefined

    if (type === 'enum') {
      const enumValues = Array.isArray(prop.options)
        ? prop.options.filter((value): value is string => typeof value === 'string')
        : []
      const normalized: Record<string, unknown> = {
        type: 'string'
      }
      if (enumValues.length > 0) {
        normalized.enum = enumValues
      }
      const description = this.extractDescription(prop)
      if (description) {
        normalized.description = description
      }
      if (typeof prop.default === 'string') {
        normalized.default = prop.default
      }
      return normalized
    }

    if (type === 'string' && ('label' in prop || 'placeholder' in prop)) {
      const normalized: Record<string, unknown> = { type: 'string' }
      const description = this.extractDescription(prop)
      if (description) {
        normalized.description = description
      }
      if (typeof prop.default === 'string') {
        normalized.default = prop.default
      }
      return normalized
    }

    return prop
  }

  private extractDescription(prop: Record<string, unknown>): string | undefined {
    if (typeof prop.description === 'string') return prop.description
    if (typeof prop.label === 'string') return prop.label
    return undefined
  }

  private endpoint(): string {
    if (this.config.provider === 'openrouter') {
      return this.config.endpoint ?? 'https://openrouter.ai/api/v1/chat/completions'
    }
    if (this.config.provider === 'openai') {
      return this.config.endpoint ?? 'https://api.openai.com/v1/chat/completions'
    }
    if (!this.config.endpoint) {
      throw new Error('Custom direct transport requires endpoint')
    }
    return this.config.endpoint
  }

  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.config.headers
    }
    if (this.config.apiKey) {
      headers['Authorization'] = `Bearer ${this.config.apiKey}`
    }
    return headers
  }

  private async clearErrorMessages(chatId: string): Promise<void> {
    const messages = await chatStorage.getMessages(chatId)
    if (!messages.length) return
    const filtered = messages.filter((m) => m.role !== MessageRole.Error)
    if (filtered.length === messages.length) return
    await chatStorage.saveMessages(chatId, filtered)
  }

  private async appendErrorMessage(chatId: string, message: string): Promise<void> {
    const errorMessage: AgentMessage = {
      id: nanoid(),
      chat: chatId,
      content: message,
      role: MessageRole.Error,
      createdAt: new Date().toISOString(),
      reasoning: '',
      toolCalls: [],
      attachments: []
    }
    await chatStorage.appendMessages(chatId, [errorMessage])
  }

  private attachChatToError(error: unknown, chat: AgentChat, fallback: string): Error & { chat: AgentChat } {
    const base = error instanceof Error ? error : new Error(fallback)
    if (!(base as any).chat) {
      Object.defineProperty(base, 'chat', {
        value: chat,
        configurable: true,
        enumerable: false,
        writable: true
      })
    }
    return base as Error & { chat: AgentChat }
  }

  private async ensureChat(payload: SendMessagePayloadWithContent): Promise<AgentChat> {
    await this.initializeStorage()

    if (payload.chatId && this.chats.has(payload.chatId)) {
      return this.chats.get(payload.chatId)!
    }

    if (payload.chatId) {
      try {
        const storedChat = await chatStorage.getChat(payload.chatId)
        if (storedChat) {
          this.chats.set(storedChat.id, storedChat)
          return storedChat
        }
      } catch (error) {
        console.error('Failed to load chat from IndexedDB:', error)
      }
    }

    const metadataTitle = payload.metadata?.['title']
    const chatTitle =
      typeof metadataTitle === 'string' && metadataTitle.trim().length > 0
        ? metadataTitle
        : this.generateLocalTitle(payload.content)
    const userId = payload.userId ?? payload.tenants?.userId ?? 'guest'
    const chat: AgentChat = {
      id: payload.chatId ?? nanoid(),
      title: chatTitle,
      type: ChatType.Chat,
      status: AgentStatus.Processing,
      contextKey: payload.contextKey,
      tenants: {
        ...(payload.tenants ?? {}),
        userId
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    this.chats.set(chat.id, chat)
    await this.persistChat(chat)
    return chat
  }

  private async persistChat(chat: AgentChat): Promise<void> {
    chat.updatedAt = new Date().toISOString()
    this.chats.set(chat.id, chat)

    chatStorage.saveChat(chat).catch((error) => {
      console.error('Failed to persist chat to IndexedDB:', error)
    })
  }

  private toOpenAIMessage(message: AgentMessage) {
    return {
      role: message.role === MessageRole.Reasoning ? MessageRole.Assistant : message.role,
      content: message.content
    }
  }

  private async callModel(messages: AgentMessage[], model?: string, contextKey?: string) {
    const selectedModel = model ?? this.config.model
    if (!selectedModel) {
      throw new Error('Model is required. Either specify in payload or config.')
    }
    const body: Record<string, unknown> = {
      model: selectedModel,
      messages: messages.map((msg) => this.toOpenAIMessage(msg))
    }

    const response = await fetch(this.endpoint(), {
      method: 'POST',
      headers: this.buildHeaders(),
      // Tools are intentionally disabled in direct mode for now.
      // This keeps the streaming path simple (text-only) and avoids
      // partially implemented tool-calling semantics on the client.
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      throw new Error(`The AI provider returned an error (status ${response.status}).`)
    }

    const json = await response.json()
    const choice = json.choices?.[0]
    const content = choice?.message?.content ?? ''
    return content
  }

  private async generateLlmTitle(input: { content: string; contextKey: string }): Promise<string> {
    const selectedModel = this.config.model
    if (!selectedModel) {
      return this.generateLocalTitle(input.content)
    }

    const prompt = [
      'You generate very short, human-friendly chat titles.',
      'Rules:',
      '- At most 8 words.',
      '- No quotes, no markdown, no emojis.',
      '- Summarize the main intent of the user message.',
      '',
      'User message:',
      input.content
    ].join('\n')

    const body: Record<string, unknown> = {
      model: selectedModel,
      messages: [
        { role: 'system', content: 'You are a title generator.' },
        { role: 'user', content: prompt }
      ]
    }

    try {
      const response = await fetch(this.endpoint(), {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        return this.generateLocalTitle(input.content)
      }

      const json = await response.json()
      const choice = json.choices?.[0]
      const content = (choice?.message?.content as string | undefined)?.trim()
      if (!content) {
        return this.generateLocalTitle(input.content)
      }
      // Hard cap length even if model misbehaves
      return this.generateLocalTitle(content)
    } catch {
      return this.generateLocalTitle(input.content)
    }
  }

  async generateTitle(input: {
    content: string
    contextKey: string
    userId?: string
    strategy?: 'local' | 'llm'
  }): Promise<string> {
    const strategy = input.strategy === 'llm' ? 'llm' : 'local'
    if (strategy === 'llm') {
      return this.generateLlmTitle({ content: input.content, contextKey: input.contextKey })
    }
    return this.generateLocalTitle(input.content)
  }

  async sendMessage(payload: SendMessagePayloadWithContent): Promise<AgentChat> {
    const chat = await this.ensureChat(payload)
    await this.clearErrorMessages(chat.id)
    const messages = await chatStorage.getMessages(chat.id)
    const userMessage: AgentMessage = {
      id: nanoid(),
      chat: chat.id,
      role: MessageRole.User,
      content: payload.content,
      createdAt: new Date().toISOString(),
      attachments: payload.attachments ?? [],
      reasoning: '',
      toolCalls: []
    }
    const messagesWithUser = [...messages, userMessage]

    try {
      const responseContent = await this.callModel(messagesWithUser, payload.model, payload.contextKey)
      const assistantMessage: AgentMessage = {
        id: nanoid(),
        chat: chat.id,
        role: MessageRole.Assistant,
        content: responseContent,
        createdAt: new Date().toISOString(),
        reasoning: '',
        toolCalls: [],
        attachments: []
      }
      await chatStorage.saveMessages(chat.id, [...messagesWithUser, assistantMessage])
      await this.persistChat(chat)
      return chat
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Failed to send message'
      await this.appendErrorMessage(chat.id, errMsg)
      await this.persistChat(chat)
      throw this.attachChatToError(error, chat, errMsg)
    }
  }

  async streamMessage(
    payload: SendMessagePayloadWithContent,
    onChunk: (chunk: StreamChunk) => void,
    options?: { streamKey?: string }
  ): Promise<{ id: string; messages: AgentMessage[] } | void> {
    const selectedModel = payload.model ?? this.config.model
    if (!selectedModel) {
      throw new Error('Model is required. Either specify in payload or config.')
    }
    const chat = await this.ensureChat(payload)
    const streamKey = options?.streamKey ?? chat.id
    await this.clearErrorMessages(chat.id)
    // Emit chat chunk immediately for new chats so UI can switch to the real id right away.
    if (!payload.chatId) {
      onChunk({ type: StreamChunkType.Chat, chatId: chat.id, chat: { ...chat } })
    }
    const userMessage: AgentMessage = {
      id: nanoid(),
      chat: chat.id,
      role: MessageRole.User,
      content: payload.content,
      createdAt: new Date().toISOString(),
      attachments: payload.attachments
    }
    await chatStorage.appendMessages(chat.id, [userMessage])
    const messagesForApi = await chatStorage.getMessages(chat.id)

    const controller = new AbortController()
    this.streamControllers.set(streamKey, controller)
    if (streamKey !== chat.id) {
      this.streamControllers.set(chat.id, controller)
    }
    let errorCaptured = false
    const captureError = async (error: unknown, fallback: string) => {
      const message = error instanceof Error ? error.message : fallback
      if (!errorCaptured) {
        await this.appendErrorMessage(chat.id, message)
        await this.persistChat(chat)
        errorCaptured = true
      }
      const baseError = this.attachChatToError(error ?? new Error(message), chat, message)
      return baseError
    }

    let response: Response
    try {
      response = await fetch(this.endpoint(), {
        method: 'POST',
        headers: {
          ...this.buildHeaders(),
          Accept: 'text/event-stream'
        },
        body: JSON.stringify({
          model: selectedModel,
          stream: true,
          messages: messagesForApi.map((msg) => this.toOpenAIMessage(msg))
        }),
        signal: controller.signal
      })
    } catch (error) {
      if ((error as any)?.name === 'AbortError') {
        await this.persistChat(chat)
        const existing = this.streamControllers.get(streamKey)
        if (existing === controller) {
          this.streamControllers.delete(streamKey)
        }
        throw error
      }
      const existing = this.streamControllers.get(streamKey)
      if (existing === controller) {
        this.streamControllers.delete(streamKey)
      }
      throw await captureError(error, 'Streaming request failed')
    }

    if (!response.ok) {
      const existing = this.streamControllers.get(streamKey)
      if (existing === controller) {
        this.streamControllers.delete(streamKey)
      }
      const message = `The AI provider returned an error (status ${response.status}).`
      throw await captureError(new Error(message), message)
    }

    if (!response.body) {
      const existing = this.streamControllers.get(streamKey)
      if (existing === controller) {
        this.streamControllers.delete(streamKey)
      }
      throw await captureError(new Error('Streaming response body missing'), 'Streaming response body missing')
    }

    const reader = response.body.getReader()
    let buffer = ''
    let accumulatedReasoning = ''
    const assistantMessage: AgentMessage = {
      id: nanoid(),
      chat: chat.id,
      role: MessageRole.Assistant,
      content: '',
      createdAt: new Date().toISOString()
    }
    await chatStorage.appendMessages(chat.id, [assistantMessage])

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const segments = buffer.split('\n\n')
        buffer = segments.pop() ?? ''
        for (const chunk of segments) {
          const line = chunk.trim()
          if (!line.startsWith('data:')) continue
          const data = line.replace(/^data:\s*/, '')
          if (data === '[DONE]') {
            onChunk({ type: StreamChunkType.Message, done: true })
            continue
          }
          try {
            const json = JSON.parse(data) as OpenAIStreamChunk
            const delta = json.choices?.[0]?.delta
            if (!delta) {
              continue
            }

            const contentDelta = delta.content ?? ''
            const reasoningDelta = delta.reasoning ?? ''

            if (contentDelta) {
              assistantMessage.content += contentDelta
              onChunk({
                type: StreamChunkType.Message,
                message: { ...assistantMessage }
              })
            }

            if (reasoningDelta) {
              accumulatedReasoning += reasoningDelta
              onChunk({
                type: StreamChunkType.Reasoning,
                reasoning: reasoningDelta
              })
            }
          } catch (error) {
            console.error('Failed to parse streaming delta', error)
          }
        }
      }
    } catch (error) {
      if ((error as any)?.name !== 'AbortError') {
        throw await captureError(error, 'Streaming response failed')
      }
    } finally {
      if (accumulatedReasoning) {
        ;(assistantMessage as AgentMessage & { reasoning?: string }).reasoning = accumulatedReasoning
      }
      const messages = await chatStorage.getMessages(chat.id)
      const lastIdx = messages.length - 1
      if (lastIdx >= 0 && messages[lastIdx].id === assistantMessage.id) {
        messages[lastIdx] = { ...assistantMessage }
        await chatStorage.saveMessages(chat.id, messages)
      }
      await this.persistChat(chat)
      const keysToDelete: string[] = []
      for (const [key, value] of this.streamControllers.entries()) {
        if (value === controller) keysToDelete.push(key)
      }
      keysToDelete.forEach((key) => this.streamControllers.delete(key))
    }

    const page = await this.getMessages(chat.id, { limit: POST_STREAM_MESSAGES_LIMIT })
    return { id: chat.id, messages: page.messages }
  }

  async listChats(params?: {
    contextKey?: string
    skip?: number
    limit?: number
    userId?: string
    multiUser?: boolean
    sortField?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<DataWithCursor<AgentChat>> {
    await this.initializeStorage()

    const sortField = params?.sortField ?? 'updatedAt'
    const sortOrder = params?.sortOrder ?? 'desc'
    const skip = params?.skip ?? 0

    try {
      const storedChats = await chatStorage.listChats({
        contextKey: params?.contextKey,
        userId: params?.userId,
        multiUser: params?.multiUser
      })
      storedChats.forEach((chat) => {
        this.chats.set(chat.id, chat)
      })

      const total = storedChats.length
      let result = storedChats

      // Apply sorting
      result.sort((a, b) => {
        const aValue = (a as any)[sortField]
        const bValue = (b as any)[sortField]
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        return sortOrder === 'asc' ? comparison : -comparison
      })

      if (skip) {
        result = result.slice(skip)
      }

      if (params?.limit) {
        result = result.slice(0, params.limit)
      }
      return {
        cursor: {
          ...(params?.skip != null ? { skip: params.skip } : {}),
          ...(params?.limit != null ? { limit: params.limit } : {}),
          total
        },
        data: result
      }
    } catch (error) {
      console.error('Failed to list chats from IndexedDB, using memory cache:', error)
      let chats = Array.from(this.chats.values())
      if (params?.contextKey) {
        chats = chats.filter((chat) => chat.contextKey === params.contextKey)
      }
      if (params?.userId) {
        const targetUserId = params.userId ?? 'guest'
        chats = chats.filter((chat) => (chat.tenants?.userId ?? 'guest') === targetUserId)
      }

      // Apply sorting
      chats.sort((a, b) => {
        const aValue = (a as any)[sortField]
        const bValue = (b as any)[sortField]
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        return sortOrder === 'asc' ? comparison : -comparison
      })

      const total = chats.length
      const sliced = (() => {
        const afterSkip = params?.skip ?? 0 ? chats.slice(params?.skip ?? 0) : chats
        return params?.limit ? afterSkip.slice(0, params.limit) : afterSkip
      })()
      return {
        cursor: {
          ...(params?.skip != null ? { skip: params.skip } : {}),
          ...(params?.limit != null ? { limit: params.limit } : {}),
          total
        },
        data: sliced
      }
    }
  }

  async getChat(chatId: string): Promise<AgentChat> {
    await this.initializeStorage()

    let chat = this.chats.get(chatId)
    if (chat) {
      return chat
    }

    try {
      const storedChat = await chatStorage.getChat(chatId)
      if (storedChat) {
        this.chats.set(storedChat.id, storedChat)
        return storedChat
      }
    } catch (error) {
      console.error('Failed to get chat from IndexedDB:', error)
    }

    throw new Error('Chat not found')
  }

  async getMessages(chatId: string, options?: GetMessagesOptions): Promise<MessagesPage> {
    const all = await chatStorage.getMessages(chatId)
    const { limit, before } = options ?? {}
    if (limit == null || limit <= 0) {
      return { messages: all, hasMore: false }
    }
    if (!before) {
      const messages = all.slice(-limit)
      return { messages, hasMore: all.length > limit }
    }
    const idx = all.findIndex((m: AgentMessage) => m.id === before)
    if (idx <= 0) return { messages: [], hasMore: false }
    const slice = all.slice(0, idx)
    const messages = slice.slice(-limit)
    return { messages, hasMore: slice.length > limit }
  }

  async deleteChat(chatId: string): Promise<void> {
    this.chats.delete(chatId)

    try {
      await chatStorage.deleteChat(chatId)
    } catch (error) {
      console.error('Failed to delete chat from IndexedDB:', error)
    }
  }

  async listAgents(): Promise<AgentProcessSummary[]> {
    return []
  }

  async controlAgent(): Promise<AgentProcessSummary> {
    throw new Error('Not supported in direct mode')
  }

  async listArtifacts(): Promise<AgentArtifact[]> {
    return []
  }

  async listActivities(): Promise<DataWithCursor<Activity>> {
    return {
      data: [],
      cursor: {
        total: 0,
        skip: 0,
        limit: 0,
      },
    };
  }

  async listActivitySourceTypes(): Promise<string[]> {
    return [];
  }

  async getActivity(): Promise<Activity | null> {
    return null;
  }

  async getActivitySummary(_params?: ActivitySummaryParams): Promise<ActivitySummary[]> {
    return [];
  }

  cancelStream(streamKey?: string) {
    if (streamKey) {
      const controller = this.streamControllers.get(streamKey)
      if (controller) {
        controller.abort()
        const keysToDelete: string[] = []
        for (const [key, value] of this.streamControllers.entries()) {
          if (value === controller) keysToDelete.push(key)
        }
        keysToDelete.forEach((key) => this.streamControllers.delete(key))
      }
      return
    }
    const controllers = new Set(this.streamControllers.values())
    for (const controller of controllers) {
      controller.abort()
    }
    this.streamControllers.clear()
  }

  async listModels(): Promise<ModelOption[]> {
    // DirectTransport uses models from config
    return this.models || []
  }

  async listAgentConfigurations(): Promise<AgentConfiguration[]> {
    return []
  }

  async upsertAgentPreferences(
    _agentName: string,
    _input: UpsertAgentPreferencesRequest
  ): Promise<UpsertAgentPreferencesResponse> {
    throw new Error('upsertAgentPreferences is not supported in direct mode')
  }

  async upsertAgentPreferencesBatch(
    _input: UpsertAgentPreferencesBatchRequest
  ): Promise<UpsertAgentPreferencesBatchResponse> {
    throw new Error('upsertAgentPreferencesBatch is not supported in direct mode')
  }
}
